const WP_IP_HOST = "35.159.115.6";
const CANONICAL_HOST = "dbi-analytics.de";
const WP_MEDIA_PROXY = "/api/wp-media";

/**
 * Returns true if the URL originates from the WordPress server (HTTP IP host).
 * These URLs must NEVER be sent to the browser directly on an HTTPS page — they
 * cause mixed-content blocking. Use toProxiedWpUrl() instead.
 */
export function isWordPressImageUrl(url: string | undefined): boolean {
  if (!url) {
    return false;
  }
  try {
    const parsed = new URL(url);
    return parsed.hostname === WP_IP_HOST;
  } catch {
    return url.includes(WP_IP_HOST);
  }
}

/**
 * Rewrites a WordPress-origin HTTP image URL to a same-origin proxy URL served
 * by /api/wp-media. The browser only ever sees HTTPS from our own origin, which
 * eliminates mixed-content blocking regardless of whether WordPress has TLS.
 *
 * Already-proxied URLs and non-WordPress URLs are returned unchanged.
 */
export function toProxiedWpUrl(url: string): string {
  // Guard against double-proxying: if the URL is already routed through wp-media,
  // return it as-is. We check the literal path prefix (before any query-encoding).
  if (url.startsWith(WP_MEDIA_PROXY)) {
    return url;
  }
  if (!isWordPressImageUrl(url)) {
    return url;
  }
  return `${WP_MEDIA_PROXY}?src=${encodeURIComponent(url)}`;
}

/**
 * Sanitizes image URLs to prevent double hostname issues, and rewrites
 * WordPress-origin URLs through the media proxy so they are always HTTPS.
 */
export function sanitizeImageUrl(url: string | undefined): string {
  if (!url) {
    return "/fallback-image.jpg";
  }

  const doubledHttpPattern = /^(https?:\/\/[^/]+)(https?:\/\/[^/]+)/;
  const match = url.match(doubledHttpPattern);
  const cleaned = match ? url.replace(match[1], "") : url;

  return toProxiedWpUrl(cleaned);
}

/**
 * Normalizes site URLs to canonical https://dbi-analytics.de domain.
 * Also rewrites legacy blog paths from /blog/posts/* to /blog/*.
 */
export function normalizeSiteUrl(url: string): string {
  const baseUrl = "https://dbi-analytics.de";

  if (!url) {
    return baseUrl;
  }

  const rawUrl = url.trim();
  const prefixedUrl = rawUrl.startsWith("http")
    ? rawUrl
    : `${baseUrl}${rawUrl.startsWith("/") ? "" : "/"}${rawUrl}`;
  const parsed = new URL(prefixedUrl);

  parsed.protocol = "https:";

  if (
    parsed.hostname === "www.dbi-analytics.de" ||
    parsed.hostname === WP_IP_HOST
  ) {
    parsed.hostname = "dbi-analytics.de";
  }

  parsed.pathname = parsed.pathname.replace(/^\/blog\/posts\//, "/blog/");

  return parsed.toString();
}

/**
 * Normalizes SEO/OG image URLs. WordPress-origin URLs are rewritten to the
 * absolute proxy URL so OG tags and JSON-LD contain a valid HTTPS address.
 * Other URLs are normalized to HTTPS canonical host as before.
 */
export function normalizeSeoImageUrl(url: string): string {
  const sanitized = sanitizeImageUrl(url);

  if (!sanitized) {
    throw new Error("SEO image URL is required");
  }

  // Already a proxy path — make it absolute for OG tags
  if (sanitized.startsWith(WP_MEDIA_PROXY)) {
    return `https://${CANONICAL_HOST}${sanitized}`;
  }

  if (sanitized.startsWith("/")) {
    return `https://${CANONICAL_HOST}${sanitized}`;
  }

  const parsed = new URL(sanitized);
  parsed.protocol = "https:";

  if (parsed.hostname === `www.${CANONICAL_HOST}`) {
    parsed.hostname = CANONICAL_HOST;
  }

  return parsed.toString();
}
